import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  LicensedComponentDto,
  ReleaseBundleArtifactDto,
  ReleaseBundleDto,
  ReleaseDto,
} from 'src/app/models/license.model';
import { ReleaseOverrideOption } from '../device-software-override.types';

export interface BundleSelectionEvent {
  option: ReleaseOverrideOption;
  bundle: ReleaseBundleDto | string;
}

@Component({
  selector: 'app-device-bundle-override-section',
  templateUrl: './device-bundle-override-section.component.html',
  styleUrl: './device-bundle-override-section.component.scss',
  standalone: false,
})
export class DeviceBundleOverrideSectionComponent {
  @Input() releaseOptions: ReleaseOverrideOption[] = [];
  @Input() loading = false;
  @Input() loadError = '';
  @Input() saving = false;
  @Input() supportedProjects: LicensedComponentDto[] = [];
  @Input() unresolvedBundles: ReleaseBundleDto[] = [];

  @Output() releaseToggled = new EventEmitter<ReleaseOverrideOption>();
  @Output() bundleSelected = new EventEmitter<BundleSelectionEvent>();
  @Output() bundlesRequested = new EventEmitter<ReleaseOverrideOption>();
  @Output() bundlesRetry = new EventEmitter<ReleaseOverrideOption>();
  @Output() unresolvedBundleRemoved = new EventEmitter<ReleaseBundleDto>();

  get selectedReleaseCount(): number {
    return this.releaseOptions.filter((option) => option.selected).length;
  }

  get releaseChoices(): ReleaseDto[] {
    return this.releaseOptions.map((option) => option.release);
  }

  get selectedReleases(): ReleaseDto[] {
    return this.releaseOptions
      .filter((option) => option.selected)
      .map((option) => option.release);
  }

  get selectedReleaseOptions(): ReleaseOverrideOption[] {
    return this.releaseOptions
      .filter((option) => option.selected)
      .sort((first, second) =>
        (first.release.name || '').localeCompare(second.release.name || ''),
      );
  }

  get sortedSupportedProjects(): LicensedComponentDto[] {
    return [...this.supportedProjects].sort((first, second) =>
      (first.componentName || first.id).localeCompare(
        second.componentName || second.id,
      ),
    );
  }

  updateSelectedReleases(releases: ReleaseDto[]): void {
    const selectedReleaseIds = new Set(releases.map((release) => release.id));
    for (const option of this.releaseOptions) {
      if (option.selected !== selectedReleaseIds.has(option.release.id)) {
        this.releaseToggled.emit(option);
      }
    }
  }

  getRelevantBundleArtifacts(
    bundle: ReleaseBundleDto,
  ): ReleaseBundleArtifactDto[] {
    const supportedProjectIds = new Set(
      this.supportedProjects.map((project) => project.id),
    );
    const artifacts =
      supportedProjectIds.size === 0
        ? (bundle.artifacts ?? [])
        : (bundle.artifacts ?? []).filter((artifact) =>
            supportedProjectIds.has(artifact.projectId),
          );

    return this.sortBundleArtifacts(artifacts);
  }

  getOtherBundleArtifacts(
    bundle: ReleaseBundleDto,
  ): ReleaseBundleArtifactDto[] {
    const supportedProjectIds = new Set(
      this.supportedProjects.map((project) => project.id),
    );
    if (supportedProjectIds.size === 0) {
      return [];
    }

    return this.sortBundleArtifacts(
      (bundle.artifacts ?? []).filter(
        (artifact) => !supportedProjectIds.has(artifact.projectId),
      ),
    );
  }

  private sortBundleArtifacts(
    artifacts: ReleaseBundleArtifactDto[],
  ): ReleaseBundleArtifactDto[] {
    return [...artifacts].sort((first, second) =>
      (first.projectName || first.projectId).localeCompare(
        second.projectName || second.projectId,
      ),
    );
  }
}
